import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clearPending, pendingIntent, saveBooking } from '@/lib/myBookings';
import { newAccessToken, newIdempotencyKey } from '@/lib/ids';
import { rpc } from '@/lib/rpc';
import { availabilitySchema, clientBookingSchema, createdBookingSchema, studioSchema } from './schemas';

export const publicKeys = {
  studio: (slug: string) => ['public', 'studio', slug] as const,
  availability: (slug: string, serviceId: string, from: string) => ['public', 'availability', slug, serviceId, from] as const,
  myBooking: (slug: string, id: string) => ['public', 'my-booking', slug, id] as const,
};

export function useStudio(slug: string) {
  return useQuery({
    queryKey: publicKeys.studio(slug),
    queryFn: () => rpc('get_public_studio', { p_slug: slug }, studioSchema),
    staleTime: 60_000,
  });
}

export function useAvailability(slug: string, serviceId: string | undefined, from: string | undefined, days = 7) {
  return useQuery({
    queryKey: publicKeys.availability(slug, serviceId ?? '', from ?? ''),
    queryFn: () => rpc('get_availability', { p_slug: slug, p_service_id: serviceId, p_from: from, p_days: days }, availabilitySchema),
    enabled: Boolean(serviceId && from),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
}

export interface BookingDraft {
  serviceId: string;
  startsAt: string;
  name: string;
  phone: string;
  car: string;
  comment: string;
}

export function useCreateBooking(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (d: BookingDraft) => {
      const fingerprint = [d.serviceId, d.startsAt, d.name, d.phone, d.car].join('|');
      const intent = pendingIntent(slug, fingerprint, () => ({
        idempotencyKey: newIdempotencyKey(),
        token: newAccessToken(),
        fingerprint,
        createdAt: new Date().toISOString(),
      }));
      const booking = await rpc(
        'create_booking',
        {
          p_slug: slug,
          p_service_id: d.serviceId,
          p_starts_at: d.startsAt,
          p_customer_name: d.name,
          p_customer_phone: d.phone,
          p_car_info: d.car,
          p_comment: d.comment,
          p_idempotency_key: intent.idempotencyKey,
          p_access_token: intent.token,
        },
        createdBookingSchema,
      );
      saveBooking(slug, {
        id: booking.id,
        token: intent.token,
        code: booking.code,
        startsAt: booking.startsAt,
        serviceName: booking.serviceName,
      });
      clearPending(slug);
      return { booking, token: intent.token };
    },
    onSettled: (_data, _err, d) => {
      void qc.invalidateQueries({ queryKey: ['public', 'availability', slug, d.serviceId] });
    },
  });
}

export function useMyBooking(slug: string, id: string, token: string | undefined) {
  return useQuery({
    queryKey: publicKeys.myBooking(slug, id),
    queryFn: () => rpc('get_my_booking', { p_slug: slug, p_booking_id: id, p_access_token: token }, clientBookingSchema),
    enabled: Boolean(token),
    retry: (count, err) => count < 2 && (err as { code?: string }).code === 'network',
  });
}

export function useCancelMyBooking(slug: string, id: string, token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      rpc('cancel_my_booking', { p_slug: slug, p_booking_id: id, p_access_token: token, p_reason: reason }, clientBookingSchema),
    onSuccess: (data) => {
      qc.setQueryData(publicKeys.myBooking(slug, id), data);
      void qc.invalidateQueries({ queryKey: ['public', 'availability', slug] });
    },
  });
}

export function useSavePushSubscription(slug: string, id: string, token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sub: { endpoint: string; p256dh: string; auth: string }) =>
      rpc(
        'save_push_subscription',
        { p_slug: slug, p_booking_id: id, p_access_token: token, p_endpoint: sub.endpoint, p_p256dh: sub.p256dh, p_auth: sub.auth },
        clientBookingSchema,
      ),
    onSuccess: (data) => qc.setQueryData(publicKeys.myBooking(slug, id), data),
  });
}
